import Phaser from "phaser";
import type { TabContentRect, TabViewContract } from "@yjsgames/phaser-ui/tab-registry";
import type { TextDirection } from "@yjsgames/i18n";
import { getTextDirection, t } from "../i18n/i18n.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";
import type { WorldTabCell } from "./worldTabData.js";

export interface WorldTabData {
  readonly cells: readonly WorldTabCell[];
}

export interface WorldTabViewOptions {
  readonly onMove: (cell: WorldTabCell) => void;
}

interface RowObjects {
  readonly worldNo: number;
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly icon: Phaser.GameObjects.Image;
  readonly title: Phaser.GameObjects.Text;
  readonly overlay: Phaser.GameObjects.Graphics;
}

const HEADER_H = 28;
const CARD_H = 64;
const CARD_GAP = 8;
const ICON_SIZE = 32;
const OVERLAY_SIZE = 14;

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T,
  visible: boolean,
): void {
  obj.setVisible(visible);
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(visible);
}

export class WorldTabView implements TabViewContract<WorldTabData> {
  private readonly title: Phaser.GameObjects.Text;
  private readonly rows: RowObjects[] = [];
  private readonly cellByWorldNo = new Map<number, WorldTabCell>();
  private readonly rect: { x: number; y: number; width: number };
  private visible = false;
  private lastData: WorldTabData | undefined;
  private dir: TextDirection = getTextDirection();

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    private readonly bottomY: number,
    private readonly opts: WorldTabViewOptions,
  ) {
    this.rect = { x, y, width };
    const L = this.layout();
    this.title = scene.add
      .text(L.title.x, L.title.y, t("world.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textPrimary,
      })
      .setOrigin(0.5, 0)
      .setDepth(3)
      .setVisible(false);
  }

  update(data: WorldTabData): void {
    this.lastData = data;
    this.rebuildRows(data);
    this.applyRowData(data);
    this.relayout(this.dir);
    this.setVisible(this.visible);
  }

  applyLanguage(): void {
    this.title.setText(t("world.title"));
    if (this.lastData) this.applyRowData(this.lastData);
  }

  relayout(dir: TextDirection): void {
    this.dir = dir;
    const L = this.layout();
    this.title.setPosition(L.title.x, L.title.y);
    this.rows.forEach((row, i) => {
      const R = this.cardLayout(i, dir);
      row.frame.setPosition(R.x, R.y).setSize(R.w, R.h);
      row.icon.setPosition(R.icon.x, R.icon.y).setDisplaySize(ICON_SIZE, ICON_SIZE);
      row.title.setPosition(R.title.x, R.title.y).setOrigin(0.5, 0).setWordWrapWidth(R.titleWidth, true);
      row.overlay.setPosition(R.overlay.x, R.overlay.y);
    });
  }

  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.relayout(getTextDirection());
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    setVisibleActive(this.title, visible);
    for (const row of this.rows) {
      setVisibleActive(row.frame, visible);
      setVisibleActive(row.icon, visible);
      setVisibleActive(row.title, visible);
      setVisibleActive(row.overlay, visible);
    }
  }

  private rebuildRows(data: WorldTabData): void {
    const sameRows =
      this.rows.length === data.cells.length &&
      this.rows.every((row, i) => row.worldNo === data.cells[i]?.worldNo);
    if (sameRows) return;

    for (const row of this.rows) {
      row.frame.destroy();
      row.icon.destroy();
      row.title.destroy();
      row.overlay.destroy();
    }
    this.rows.length = 0;

    data.cells.forEach((cell, index) => {
      const R = this.cardLayout(index, this.dir);
      const frame = this.scene.add
        .rectangle(R.x, R.y, R.w, R.h, PALETTE_NUM.cellBg, 0.88)
        .setOrigin(0, 0)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28)
        .setDepth(2)
        .setInteractive({ useHandCursor: true });
      frame.on("pointerup", () => this.opts.onMove(this.cellByWorldNo.get(cell.worldNo) ?? cell));
      const icon = this.scene.add.image(R.icon.x, R.icon.y, cell.iconKey).setDisplaySize(ICON_SIZE, ICON_SIZE).setDepth(3);
      const title = this.scene.add
        .text(R.title.x, R.title.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textPrimary,
          align: "center",
          wordWrap: { width: R.titleWidth },
        })
        .setOrigin(0.5, 0)
        .setDepth(3);
      const overlay = this.scene.add.graphics().setDepth(4).setPosition(R.overlay.x, R.overlay.y);
      this.rows.push({ worldNo: cell.worldNo, frame, icon, title, overlay });
    });
  }

  private applyRowData(data: WorldTabData): void {
    data.cells.forEach((cell, i) => {
      const row = this.rows[i];
      if (!row) return;
      this.cellByWorldNo.set(cell.worldNo, cell);
      row.icon.setTexture(cell.iconKey);
      row.title.setText(`${cell.worldNo}. ${t(cell.nameKey)}`);
      row.frame.setStrokeStyle(
        cell.status === "current" ? 2 : 1,
        cell.status === "current" ? PALETTE_NUM.accentGold : PALETTE_NUM.gridPathGlow,
        cell.status === "current" ? 0.86 : 0.28,
      );
      row.frame.setAlpha(cell.status === "locked" ? 0.58 : 1);
      row.icon.setAlpha(cell.status === "locked" ? 0.42 : 1);
      row.title.setAlpha(cell.status === "locked" ? 0.56 : 1);

      row.overlay.clear();
      if (cell.status === "cleared") {
        this.drawCheckOverlay(row.overlay);
      } else if (cell.status === "locked") {
        this.drawLockOverlay(row.overlay);
      }
    });
  }

  /** Green checkmark circle overlay drawn at (0,0) relative to overlay Graphics position. */
  private drawCheckOverlay(g: Phaser.GameObjects.Graphics): void {
    const r = OVERLAY_SIZE / 2;
    g.fillStyle(PALETTE_NUM.positive, 0.92);
    g.fillCircle(0, 0, r);
    // Checkmark stroke
    g.lineStyle(2, 0xffffff, 1);
    g.beginPath();
    g.moveTo(-r * 0.35, 0);
    g.lineTo(-r * 0.05, r * 0.32);
    g.lineTo(r * 0.38, -r * 0.28);
    g.strokePath();
  }

  /** Lock icon overlay drawn at (0,0) relative to overlay Graphics position. */
  private drawLockOverlay(g: Phaser.GameObjects.Graphics): void {
    const r = OVERLAY_SIZE / 2;
    g.fillStyle(0x888888, 0.82);
    g.fillCircle(0, 0, r);
    // Simple lock body
    const bw = r * 0.7;
    const bh = r * 0.55;
    g.fillStyle(0xcccccc, 1);
    g.fillRect(-bw, -bh * 0.15, bw * 2, bh);
    // Lock shackle (arc)
    g.lineStyle(1.5, 0xcccccc, 1);
    g.beginPath();
    g.arc(0, -bh * 0.15, bw * 0.55, Math.PI, 0, false);
    g.strokePath();
  }

  private layout(): { readonly title: { readonly x: number; readonly y: number } } {
    return { title: { x: this.rect.x + this.rect.width / 2, y: this.rect.y } };
  }

  private cardLayout(
    index: number,
    _dir: TextDirection,
  ): {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
    readonly icon: { readonly x: number; readonly y: number };
    readonly title: { readonly x: number; readonly y: number };
    readonly titleWidth: number;
    readonly overlay: { readonly x: number; readonly y: number };
  } {
    const cols = this.rect.width >= 280 ? 2 : 1;
    const col = index % cols;
    const row = Math.floor(index / cols);
    const cardW = (this.rect.width - CARD_GAP * (cols - 1)) / cols;
    const x = this.rect.x + col * (cardW + CARD_GAP);
    const y = Math.min(this.bottomY - CARD_H, this.rect.y + HEADER_H + row * (CARD_H + CARD_GAP));
    const centerX = x + cardW / 2;
    return {
      x,
      y,
      w: cardW,
      h: CARD_H,
      icon: { x: centerX, y: y + 42 },
      title: { x: centerX, y: y + 6 },
      titleWidth: Math.max(64, cardW - 12),
      overlay: { x: centerX + ICON_SIZE / 2 - 2, y: y + 42 - ICON_SIZE / 2 + 2 },
    };
  }
}
